import { useCallback } from "react"
import { createStyles } from "@design-system/core"

import { UseLinkProps, UseLinkResult } from "./types"

const styles = createStyles((theme) => ({
  link: {
    cursor: "pointer",
    color: theme.textColors.link,
  },
}))

export function useLink<P>(props = {} as UseLinkProps<P>): UseLinkResult<P> {
  const {
    onClick,
    onKeyPress,
    anchorless,
    href,
    style,
    target: targetProp,
    ...others
  } = props

  const htmlProps = others as P

  const target = !anchorless ? targetProp : undefined

  const handleClick = useCallback(
    (e: React.MouseEvent | React.KeyboardEvent) => {
      onClick?.(e as React.MouseEvent)

      if (e.defaultPrevented) {
        return
      }

      // do nothing for normal link
      if (!anchorless || !href) {
        return
      }

      const ctrl = e.metaKey || e.ctrlKey
      const newWindow = target === "_blank"

      e.preventDefault()

      if (ctrl) {
        if (newWindow || ctrl) {
          window.open(href)
        } else {
          window.open(href, "_self")
        }
      }
    },
    [onClick, anchorless, target, href],
  )

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (onKeyPress) {
      onKeyPress?.(e)
    }

    if (!e.defaultPrevented && e.key === "Enter") {
      handleClick(e)
      e.preventDefault()
    }
  }

  if (!href) {
    return props
  }

  return {
    as: "a",
    ...htmlProps,
    href,
    anchorless,
    onClick: handleClick,
    onKeyPress: handleKeyPress,
    style: [styles.link, style],
    target: !anchorless && target,
  } as UseLinkResult<P>
}
